import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import { useResetUserPassword } from '../../hooks/api/useUsers'
import { Booker } from '../../types'

interface ResetPasswordDialogProps {
  open: boolean
  onClose: () => void
  user: Booker | null
}

const ResetPasswordDialog = ({ open, onClose, user }: ResetPasswordDialogProps) => {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({})
  const resetPasswordMutation = useResetUserPassword()

  useEffect(() => {
    setNewPassword('')
    setConfirmPassword('')
    setErrors({})
  }, [open, user?._id])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!user || resetPasswordMutation.isPending) return

    const nextErrors: { newPassword?: string; confirmPassword?: string } = {}
    if (newPassword.length < 6) nextErrors.newPassword = 'Password must be at least 6 characters'
    if (newPassword.length > 1024) nextErrors.newPassword = 'Password must be at most 1024 characters'
    if (!confirmPassword) nextErrors.confirmPassword = 'Confirm the new password'
    else if (confirmPassword !== newPassword) nextErrors.confirmPassword = 'Passwords do not match'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    try {
      await resetPasswordMutation.mutateAsync({
        id: user._id,
        payload: { new_password: newPassword, confirm_password: confirmPassword },
      })
      onClose()
    } catch {
      // The mutation displays the API error.
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="New Password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => {
                setNewPassword(event.target.value)
                setErrors((current) => ({ ...current, newPassword: undefined }))
              }}
              error={Boolean(errors.newPassword)}
              helperText={errors.newPassword}
              required
              fullWidth
            />
            <TextField
              label="Confirm Password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value)
                setErrors((current) => ({ ...current, confirmPassword: undefined }))
              }}
              error={Boolean(errors.confirmPassword)}
              helperText={errors.confirmPassword}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={resetPasswordMutation.isPending}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={resetPasswordMutation.isPending || !user}>
            Reset Password
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default ResetPasswordDialog
